import React from 'react';
import PropTypes from 'prop-types';
import Card from 'common/components/card';
import CardContent from 'common/components/card-content';
import CardConfig from 'common/components/card-config';
import CardHeader from 'common/components/card-header';
import MapboxFixedMap from 'common/components/mapbox-fixed-map';
import TenantStore from 'common/stores/tenant-store';
import * as LucidActions from 'common/actions/actions';
import {areaUnitter} from 'common/utils/units-util';
import {getBuildingInfoCardImageSrc} from 'common/utils/statics-util';
import {nt} from 'common/utils/translation-util';
import '../building-info-card/index.scss';

/* TODO i18n */
const attributes = {
  buildings: {
    icon: 'address-icon.svg',
    unitter: (count) => nt('building location', 'building locations', count),
  },
  spaces: {
    icon: 'spaces-icon.svg',
    unitter: (count) => nt('building space', 'building spaces', count),
  },
  area: {
    icon: 'square-feet-icon.svg',
    unitter: areaUnitter,
    getter: 'getArea',
  },
};

export default class TenantInfoCard extends React.Component {
  static propTypes = {
    connectDragSource: PropTypes.func,
    data: PropTypes.object,
    disableConfig: PropTypes.bool,
    reportId: PropTypes.number,
    showConfig: PropTypes.bool,
    showTenantName: PropTypes.bool,
    tenantId: PropTypes.number,
  };

  static defaultProps = {
    connectDragSource: (x) => x,
    data: null,
    disableConfig: true,
    reportId: null,
    showConfig: false,
    showTenantName: false,
    tenantId: null,
  };

  constructor(props, context) {
    super(props, context);
    this.onChange = this.onChange.bind(this);

    // Starts as loading if data did not get passed in as a prop
    const loading = !this.props.data;
    this.state = {...this.getStateFromStores(), loading};
  }

  UNSAFE_componentWillMount() {
    if (!this.props.data) {
      LucidActions.getTenant(this.props.tenantId);
    }
  }

  componentDidMount() {
    TenantStore.addChangeListener(this.onChange);
  }

  componentWillUnmount() {
    TenantStore.removeChangeListener(this.onChange);
  }

  getArea() {
    // Returns total area for this tenant across all buildings
    const areas = this.state.spaces.map((space) => Number(space.convertedArea));
    return Math.round(areas.reduce((datum1, datum2) => datum1 + datum2, 0));
  }

  getStateFromStores() {
    // If the data is coming directly from reports framework (e.g. not API),
    // Use that (passed as prop), otherwise get from store:
    const tenant = this.props.data || TenantStore.getTenantDetails() || {};
    tenant.spaces = tenant.spaces || [];
    tenant.buildings = tenant.buildings || [];
    return tenant;
  }

  generateLocationList() {
    const locationList = [];
    for (const building of this.state.buildings || []) {
      const loc = building.location;
      if (!loc || loc.lan === null || loc.lon === null) {
        continue; // Skip buildings with invalid locations
      }
      locationList.push({latitude: loc.lat, longitude: loc.lon});
    }
    return locationList;
  }

  showAttribute(attrName) {
    // Helper function for formatting an attribute of Tenant
    const config = attributes[attrName];
    const imageSrc = getBuildingInfoCardImageSrc(config.icon);
    let value = this.state[attrName];
    if (config.getter) {
      value = this[config.getter]();
    }

    if (Array.isArray(value)) {
      // Displaying the length of this, nothing else
      value = value.length;
    }

    return (
      <div className={'BuildingInfoCard-attr arrange'}>
        <span className={'BuildingInfoCard-attrIconHolder arrange-sizeFit'}>
          <img className={'BuildingInfoCard-attrIcon'} src={imageSrc} />
        </span>
        <span className={'BuildingInfoCard-attrText arrange-sizeFill'}>
          {value}{' '}
          {config.unitter ? config.unitter(value, this.state.buildings[0]) : ''}
        </span>
      </div>
    );
  }

  renderMap(locationList) {
    return (
      <div className={'mapbox-container'}>
        <MapboxFixedMap
          height={100}
          markerList={locationList}
          markerSize={'s'}
          maxWidth={900}
        />
      </div>
    );
  }

  render() {
    // Generate the list of lat/lon pairs for the buildings
    const locationList = this.generateLocationList();

    // Only show the map if there is at least 1 location
    const map =
      locationList.length > 0
        ? this.props.connectDragSource(this.renderMap(locationList))
        : null;

    const config = this.props.disableConfig ? null : (
      <CardConfig disableDownload={true} reportId={this.props.reportId} />
    );

    return (
      <div className={'TenantInfoCard'}>
        <Card className={'Card--corner-menu'} loading={this.state.loading}>
          {map}
          {config}
          {this.state.image ? (
            <div className={'InfoCard-logoWrapper'}>
              <img src={this.state.image} />
            </div>
          ) : null}
          {this.props.showTenantName ? (
            <CardHeader
              disableConfig={true}
              reportId={this.props.reportId}
              title={this.state.name}
            />
          ) : null}
          <CardContent>
            <div
              className={'BuildingInfoCard-info clearfix'}
              style={this.props.showTenantName ? null : {paddingTop: 20}}>
              {this.showAttribute('buildings')}
              {this.showAttribute('spaces')}
              {this.showAttribute('area')}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  onChange() {
    this.setState({
      ...this.getStateFromStores(),
      loading: false,
    });
  }
}
